import { Box } from '@mui/material';
import { useMemo, useState } from 'react';

import type { Member } from '../types';

type MemberFilterSidebarProps = {
  members: Member[];
  selectedMemberIds: string[] | null; // null = all visible
  onSelectedMemberIdsChange: (ids: string[] | null) => void;
};

// ---- Three-state checkbox ----

type CheckState = 'checked' | 'unchecked' | 'indeterminate';

function masterState(members: Member[], selected: string[] | null): CheckState {
  if (selected === null) return 'checked';
  if (selected.length === 0) return 'unchecked';
  if (selected.length === members.length) return 'checked';
  return 'indeterminate';
}

function Checkbox({ state, color }: { state: CheckState; color?: string }) {
  const bg =
    state === 'unchecked'
      ? 'transparent'
      : color ?? '#2a3a22';

  return (
    <Box
      sx={{
        width: 16,
        height: 16,
        border: `2px solid ${state === 'unchecked' ? '#888' : (color ?? '#2a3a22')}`,
        borderRadius: '3px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        background: state === 'unchecked' ? 'transparent' : bg,
        transition: 'all 0.1s',
      }}
    >
      {state === 'checked' && (
        <Box component="span" sx={{ color: '#fff', fontSize: 11, fontWeight: 700, lineHeight: 1 }}>
          ✓
        </Box>
      )}
      {state === 'indeterminate' && (
        <Box sx={{ width: 8, height: 2, background: '#fff', borderRadius: 1 }} />
      )}
    </Box>
  );
}

// ---- Sidebar ----

export function MemberFilterSidebar({
  members,
  selectedMemberIds,
  onSelectedMemberIdsChange,
}: MemberFilterSidebarProps) {
  const [search, setSearch] = useState('');

  const sortedMembers = useMemo(
    () => [...members].sort((a, b) => a.name.localeCompare(b.name)),
    [members],
  );

  const filteredMembers = useMemo(() => {
    if (!search.trim()) return sortedMembers;
    const q = search.toLowerCase();
    return sortedMembers.filter((m) => m.name.toLowerCase().includes(q));
  }, [sortedMembers, search]);


  function isMemberSelected(id: string): boolean {
    if (selectedMemberIds === null) return true;
    return selectedMemberIds.includes(id);
  }

  function toggleMaster() {
    const state = masterState(members, selectedMemberIds);
    if (state === 'checked') {
      onSelectedMemberIdsChange([]);
    } else {
      onSelectedMemberIdsChange(null);
    }
  }

  function toggleMember(id: string) {
    const allIds = members.map((m) => m.id);
    const current = selectedMemberIds === null ? allIds : selectedMemberIds;
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    // If all selected, normalise to null
    onSelectedMemberIdsChange(next.length === allIds.length ? null : next);
  }

  const mState = masterState(members, selectedMemberIds);

  return (
    <Box
      sx={{
        width: 240,
        flexShrink: 0,
        background: '#fff',
        borderRight: '1px solid #e8e5e0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          padding: '12px 16px 6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: '#888',
            fontWeight: 600,
          }}
        >
          Members
        </Box>
      </Box>

      {/* Search */}
      <Box sx={{ padding: '0 12px 8px' }}>
        <Box
          component="input"
          placeholder="Search members…"
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          sx={{
            width: '100%',
            padding: '6px 10px',
            border: '1px solid #e0ddd8',
            borderRadius: '5px',
            fontSize: 11,
            fontFamily: 'inherit',
            background: '#fafaf8',
            outline: 'none',
            boxSizing: 'border-box',
            '&:focus': { borderColor: '#2a3a22' },
          }}
        />
      </Box>

      {/* Member list */}
      <Box sx={{ overflowY: 'auto', flex: 1, pb: '8px' }}>
        {/* All-members master row */}
        <Box
          onClick={toggleMaster}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px 16px',
            cursor: 'pointer',
            userSelect: 'none',
            borderBottom: '1px solid #f0ede8',
            mb: '4px',
            fontWeight: 500,
            fontSize: 12,
            '&:hover': { background: '#fafaf8' },
          }}
        >
          <Checkbox state={mState} />
          <Box sx={{ color: '#222', flex: 1 }}>All members</Box>
        </Box>

        {/* Flat alphabetical list */}
        {filteredMembers.map((m) => (
          <MemberRow
            key={m.id}
            member={m}
            selected={isMemberSelected(m.id)}
            onToggle={() => toggleMember(m.id)}
          />
        ))}

        {filteredMembers.length === 0 && (
          <Box sx={{ px: 2, py: 1, fontSize: 11, color: '#aaa' }}>No matches</Box>
        )}
      </Box>
    </Box>
  );
}

function MemberRow({
  member,
  selected,
  onToggle,
}: {
  member: Member;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '6px 16px',
        cursor: 'pointer',
        userSelect: 'none',
        fontSize: 12,
        '&:hover': { background: '#fafaf8' },
      }}
    >
      <Checkbox state={selected ? 'checked' : 'unchecked'} color={member.color} />
      <Box
        sx={{
          color: '#333',
          flex: 1,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {member.name}
      </Box>
    </Box>
  );
}
